import axios from 'axios';
import configurations from '../../configurations';

const buildQueryString = (limit = 20, offset = 0, sort) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  params.append('offset', offset);
  if (sort) params.append('sort', sort);
  return params.toString();
};

const buildRequestBody = (filterParams = {}) => {
  const body = {};

  // Always-required fields for members search
  if (filterParams.statuses) body.statuses = filterParams.statuses;
  if (filterParams.agentId) body.agentId = String(filterParams.agentId).trim();
  if (filterParams.allStatus !== undefined) body.allStatus = Boolean(filterParams.allStatus);
  
  const optionalStringFields = [
    'programId',
    'memberId',
    'firstName',
    'lastName',
    'agentId',
    'agentName',
    'programName',
    'employerId',
    'daysInStatus',
    'daysToTermination'
  ];

  optionalStringFields.forEach((key) => {
    const raw = filterParams[key];
    if (raw === undefined || raw === null) return;
    const value = String(raw).trim();
    if (value !== '') body[key] = value;
  });

  const optionalArrayFields = [
    'paidThroughDate',
    'programEndDate',
    'cancellationReqDate',
    'programBeginningDate',
  ];

  optionalArrayFields.forEach((key) => {
    const value = filterParams[key];
    if (Array.isArray(value) && value.length > 0) body[key] = value;
  });

  return body;
};

export const searchEnrolledMembers = async (params = {}) => {
  if (!params.agentId) {
    throw new Error('agentId is mandatory');
  }
  if (!params.statuses) {
    throw new Error('status is mandatory');
  }

  const {
    agentId,
    statuses,
    limit = 20,
    offset = 0,
    sort,
    ...filterParams
  } = params;

  const queryString = buildQueryString(limit, offset, sort);
  const requestBody = buildRequestBody({
    agentId,
    statuses,
    allStatus : process.env.REACT_APP_PORTAL === 'employer' ? false : true,
    ...filterParams,
  });

  const url = `${process.env.REACT_APP_BASE_URL}/enrollment/enrollMembers/search?${queryString}`;

  const response = await axios.post(url, requestBody, {
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      Accept: 'application/json, text/plain, */*',
    },
  });

  const payload = response.data || {};
  const normalizedData = Array.isArray(payload.data)
    ? payload.data
    : Array.isArray(payload.response)
      ? payload.response
      : [];

  const meta = payload?.errors || payload?.errros || {};
  const derivedCountFromPages =
    meta?.totalNumberOfPages && meta?.limit
      ? Number(meta.totalNumberOfPages) * Number(meta.limit)
      : 0;

  const normalizedTotalCount =
    Number(
      payload.totalCount ??
        meta?.count ??
        meta?.totalCount ??
        payload?.errors?.count ??
        payload?.errors?.totalCount ??
        payload?.errros?.count ??
        payload?.errros?.totalCount ??
        derivedCountFromPages ??
        0,
    ) || 0;

  return {
    data: normalizedData,
    totalCount: normalizedTotalCount,
    raw: payload,
  };
};

const buildAgentMembersQueryString = (limit = 10, offset = 0, sort) => {
  const params = new URLSearchParams();
  params.append('limit', limit);
  params.append('offset', offset);
  if (sort) params.append('sort', sort);
  return params.toString();
};

const buildAgentMembersRequestBody = (filterParams = {}) => {
  const body = {};

  if (filterParams.clientId !== undefined && filterParams.clientId !== null && String(filterParams.clientId).trim() !== '') {
    body.clientId = String(filterParams.clientId).trim();
  }
  if (filterParams.agentId) body.agentId = String(filterParams.agentId).trim();
  if (filterParams.statuses) body.statuses = filterParams.statuses;
  if (filterParams.isPrimary !== undefined) body.isPrimary = filterParams.isPrimary;
  if (filterParams.activeFlag !== undefined) body.activeFlag = Boolean(filterParams.activeFlag);
  if (filterParams.enrollFlag !== undefined) body.enrollFlag = Boolean(filterParams.enrollFlag);

  ['memberId', 'firstName', 'lastName', 'employerId'].forEach((key) => {
    const raw = filterParams[key];
    if (raw === undefined || raw === null) return;
    const value = String(raw).trim();
    if (value !== '') body[key] = value;
  });

  return body;
};

// Backs the ENROLLMENTS tab (prospects + members in a single unified list).
export const searchAgentMembers = async (params = {}) => {
  if (!params.clientId) {
    throw new Error('clientId is mandatory');
  }

  const {
    clientId,
    agentId,
    limit = 10,
    offset = 0,
    sort,
    ...filterParams
  } = params;

  const queryString = buildAgentMembersQueryString(limit, offset, sort);
  const requestBody = buildAgentMembersRequestBody({
    clientId,
    agentId,
    ...filterParams,
  });

  const url = `${process.env.REACT_APP_VERA_BASE_URL}/agent/members/search?${queryString}`;

  const response = await axios.post(url, requestBody, {
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      Accept: 'application/json, text/plain, */*',
    },
  });

  const payload = response.data || {};
  const normalizedData = Array.isArray(payload.response)
    ? payload.response
    : Array.isArray(payload.data)
      ? payload.data
      : [];

  const meta = payload?.errros || payload?.errors || {};
  const derivedCountFromPages =
    meta?.totalNumberOfPages && meta?.limit
      ? Number(meta.totalNumberOfPages) * Number(meta.limit)
      : 0;

  const normalizedTotalCount =
    Number(meta?.count ?? meta?.totalCount ?? derivedCountFromPages ?? 0) || 0;

  return {
    data: normalizedData,
    totalCount: normalizedTotalCount,
    raw: payload,
  };
};

export const getPlansByChannelId = async (channelId) => {
  const id = Number(channelId);
  if (!id) {
    throw new Error('channelId is required');
  }

  const url = `${process.env.REACT_APP_enrollment_base_url}/plan/getPlans/${id}`;
  const response = await axios.get(url, {
    headers: {
      Accept: 'application/json, text/plain, */*',
    },
  });

  const payload = response.data || {};
  const list = Array.isArray(payload.response) ? payload.response : [];
  return list;
};

export default {
  searchEnrolledMembers,
  searchAgentMembers,
  getPlansByChannelId,
};
